import React, { useState, useEffect } from 'react';
import { Card, Form, Input, Button, DatePicker, InputNumber, Table, Space, message, Modal, Typography, Divider, Alert, Tag } from 'antd';
import { PlusOutlined, DeleteOutlined, EditOutlined, DollarOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import api from '../../services/api';
import paymentScheduleNotificationService from '../../services/paymentScheduleNotificationService';

const { Title, Text } = Typography;
const { TextArea } = Input;

function VendorScheduleEditor({ bookingId, onScheduleUpdated }) {
    const [form] = Form.useForm();
    const [schedule, setSchedule] = useState(null);
    const [loading, setLoading] = useState(false);
    const [editingMilestone, setEditingMilestone] = useState(null);
    const [showAdvanceModal, setShowAdvanceModal] = useState(false);
    const [advanceForm] = Form.useForm();

    useEffect(() => {
        loadSchedule();
    }, [bookingId]);

    const loadSchedule = async () => {
        try {
            setLoading(true);
            const response = await api.getPaymentSchedule(bookingId);
            setSchedule(response.data);
        } catch (error) {
            message.error('Failed to load payment schedule');
        } finally {
            setLoading(false);
        }
    };

    const handleAddMilestone = () => {
        const values = form.getFieldsValue();
        const newMilestone = {
            name: values.name,
            description: values.description,
            amount: values.amount,
            due_date: values.due_date?.format('YYYY-MM-DD'),
            percentage: values.percentage,
        };

        const updatedMilestones = [...(schedule?.milestones || []), newMilestone];
        const updatedSchedule = {
            ...schedule,
            milestones: updatedMilestones,
            total_amount: updatedMilestones.reduce((sum, m) => sum + (m.amount || 0), 0)
        };

        setSchedule(updatedSchedule);
        form.resetFields();
    };

    const handleUpdateMilestone = (index) => {
        const values = form.getFieldsValue();
        const updatedMilestones = [...(schedule?.milestones || [])];
        updatedMilestones[index] = {
            ...updatedMilestones[index],
            name: values.name,
            description: values.description,
            amount: values.amount,
            due_date: values.due_date?.format('YYYY-MM-DD'),
            percentage: values.percentage,
        };

        const updatedSchedule = {
            ...schedule,
            milestones: updatedMilestones,
            total_amount: updatedMilestones.reduce((sum, m) => sum + (m.amount || 0), 0)
        };

        setSchedule(updatedSchedule);
        setEditingMilestone(null);
        form.resetFields();
    };

    const handleDeleteMilestone = (index) => {
        const updatedMilestones = (schedule?.milestones || []).filter((_, i) => i !== index);
        const updatedSchedule = {
            ...schedule,
            milestones: updatedMilestones,
            total_amount: updatedMilestones.reduce((sum, m) => sum + (m.amount || 0), 0)
        };

        setSchedule(updatedSchedule);
    };

    const handleSaveSchedule = async () => {
        try {
            setLoading(true);
            await api.updatePaymentSchedule(bookingId, schedule);
            await paymentScheduleNotificationService.sendScheduleUpdatedNotification(bookingId, schedule);
            message.success('Payment schedule updated successfully');
            onScheduleUpdated?.();
        } catch (error) {
            message.error('Failed to update payment schedule');
        } finally {
            setLoading(false);
        }
    };

    const handleRequestAdvance = async () => {
        try {
            const values = await advanceForm.validateFields();
            await api.requestAdvancePayment(bookingId, values);
            await paymentScheduleNotificationService.sendAdvancePaymentRequestNotification(bookingId, values);
            message.success('Advance payment request sent to customer');
            setShowAdvanceModal(false);
            advanceForm.resetFields();
        } catch (error) {
            message.error('Failed to send advance payment request');
        }
    };

    const milestoneColumns = [
        {
            title: 'Milestone',
            dataIndex: 'name',
            key: 'name',
            render: (text, record, index) => (
                <div>
                    <div className="font-medium">{text}</div>
                    <div className="text-sm text-gray-500">{record.description}</div>
                </div>
            ),
        },
        {
            title: 'Amount',
            dataIndex: 'amount',
            key: 'amount',
            render: (amount) => `₹${amount?.toLocaleString() || 0}`,
        },
        {
            title: 'Percentage',
            dataIndex: 'percentage',
            key: 'percentage',
            render: (percentage) => `${percentage || 0}%`,
        },
        {
            title: 'Due Date',
            dataIndex: 'due_date',
            key: 'due_date',
            render: (date) => date ? dayjs(date).format('MMM DD, YYYY') : '-',
        },
        {
            title: 'Status',
            key: 'status',
            render: (_, record, index) => {
                const isPaid = record.paid_at;
                const isOverdue = record.due_date && dayjs(record.due_date).isBefore(dayjs()) && !isPaid;
                
                if (isPaid) {
                    return <Tag color="green">Paid</Tag>;
                } else if (isOverdue) {
                    return <Tag color="red">Overdue</Tag>;
                } else {
                    return <Tag color="orange">Pending</Tag>;
                }
            },
        },
        {
            title: 'Actions',
            key: 'actions',
            render: (_, record, index) => (
                <Space>
                    <Button
                        size="small"
                        icon={<EditOutlined />}
                        onClick={() => {
                            setEditingMilestone(index);
                            form.setFieldsValue({
                                name: record.name,
                                description: record.description,
                                amount: record.amount,
                                due_date: record.due_date ? dayjs(record.due_date) : null,
                                percentage: record.percentage,
                            });
                        }}
                    >
                        Edit
                    </Button>
                    <Button
                        size="small"
                        danger
                        icon={<DeleteOutlined />}
                        onClick={() => handleDeleteMilestone(index)}
                    >
                        Delete
                    </Button>
                </Space>
            ),
        },
    ];

    if (loading && !schedule) {
        return <div className="text-center py-8">Loading payment schedule...</div>;
    }

    return (
        <div className="space-y-6">
            <Card>
                <div className="flex justify-between items-center mb-4">
                    <Title level={4} className="mb-0">Payment Schedule</Title>
                    <Space>
                        <Button
                            type="primary"
                            icon={<DollarOutlined />}
                            onClick={() => setShowAdvanceModal(true)}
                        >
                            Request Advance
                        </Button>
                        <Button
                            type="primary"
                            onClick={handleSaveSchedule}
                            loading={loading}
                        >
                            Save Schedule
                        </Button>
                    </Space>
                </div>

                {schedule && (
                    <div className="mb-6">
                        <Alert
                            message={`Total Amount: ₹${schedule.total_amount?.toLocaleString() || 0}`}
                            type="info"
                            showIcon
                        />
                    </div>
                )}

                <Form form={form} layout="vertical" className="mb-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
                        <Form.Item
                            name="name"
                            label="Milestone Name"
                            rules={[{ required: true, message: 'Please enter milestone name' }]}
                        >
                            <Input placeholder="e.g., 50% Advance" />
                        </Form.Item>

                        <Form.Item
                            name="amount"
                            label="Amount (₹)"
                            rules={[{ required: true, message: 'Please enter amount' }]}
                        >
                            <InputNumber
                                placeholder="0"
                                style={{ width: '100%' }}
                                formatter={value => `₹ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                                parser={value => value.replace(/₹\s?|(,*)/g, '')}
                            />
                        </Form.Item>

                        <Form.Item
                            name="percentage"
                            label="Percentage (%)"
                        >
                            <InputNumber
                                placeholder="0"
                                style={{ width: '100%' }}
                                min={0}
                                max={100}
                                formatter={value => `${value}%`}
                                parser={value => value.replace('%', '')}
                            />
                        </Form.Item>

                        <Form.Item
                            name="due_date"
                            label="Due Date"
                        >
                            <DatePicker
                                style={{ width: '100%' }}
                                placeholder="Select date"
                            />
                        </Form.Item>

                        <Form.Item label=" ">
                            <Button
                                type="dashed"
                                icon={<PlusOutlined />}
                                onClick={editingMilestone !== null ? () => handleUpdateMilestone(editingMilestone) : handleAddMilestone}
                                block
                            >
                                {editingMilestone !== null ? 'Update Milestone' : 'Add Milestone'}
                            </Button>
                        </Form.Item>
                    </div>

                    <Form.Item
                        name="description"
                        label="Description"
                    >
                        <TextArea
                            placeholder="Describe what this milestone covers..."
                            rows={2}
                        />
                    </Form.Item>
                </Form>

                {schedule?.milestones && schedule.milestones.length > 0 && (
                    <Table
                        columns={milestoneColumns}
                        dataSource={schedule.milestones.map((milestone, index) => ({ ...milestone, key: index }))}
                        pagination={false}
                        size="small"
                    />
                )}
            </Card>

            {/* Advance Payment Request Modal */}
            <Modal
                title="Request Advance Payment"
                open={showAdvanceModal}
                onCancel={() => setShowAdvanceModal(false)}
                onOk={handleRequestAdvance}
                okText="Send Request"
            >
                <Form form={advanceForm} layout="vertical">
                    <Form.Item
                        name="amount"
                        label="Advance Amount (₹)"
                        rules={[{ required: true, message: 'Please enter advance amount' }]}
                    >
                        <InputNumber
                            placeholder="0"
                            style={{ width: '100%' }}
                            formatter={value => `₹ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                            parser={value => value.replace(/₹\s?|(,*)/g, '')}
                        />
                    </Form.Item>

                    <Form.Item
                        name="reason"
                        label="Reason for Advance"
                        rules={[{ required: true, message: 'Please provide reason' }]}
                    >
                        <TextArea
                            placeholder="Explain why advance payment is needed..."
                            rows={3}
                        />
                    </Form.Item>

                    <Form.Item
                        name="due_date"
                        label="Payment Due Date"
                        rules={[{ required: true, message: 'Please select due date' }]}
                    >
                        <DatePicker
                            style={{ width: '100%' }}
                            placeholder="Select date"
                        />
                    </Form.Item>
                </Form>
            </Modal>
        </div>
    );
}

export default VendorScheduleEditor;
